import { useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import client from '@/utils/axios'
import type { IconName } from '@/components/atoms/KBIcon/KBIcon'

// Types matching backend OnboardingService
export enum OnboardingStep {
  CREATE_LOCATION = 'CREATE_LOCATION',
  ADD_RESOURCES = 'ADD_RESOURCES',
  UPLOAD_FLOOR_PLAN = 'UPLOAD_FLOOR_PLAN',
  INVITE_TEAM = 'INVITE_TEAM',
  MAKE_BOOKING = 'MAKE_BOOKING',
  INSTALL_CALENDAR_APP = 'INSTALL_CALENDAR_APP',
  LINK_RESOURCE_CALENDAR = 'LINK_RESOURCE_CALENDAR',
}

export interface OnboardingStepStatus {
  key: OnboardingStep
  completed: boolean
  completedAt?: string
}

export interface OnboardingStatus {
  spaceId: number
  steps: OnboardingStepStatus[]
  completedSteps: number
  totalSteps: number
  progressPercent: number
  isComplete: boolean
  isDismissed: boolean
}

/**
 * Step configurations for UI display
 */
export const ONBOARDING_STEP_CONFIG: Record<
  OnboardingStep,
  {
    titleKey: string
    descriptionKey: string
    href: string
    icon: IconName
  }
> = {
  [OnboardingStep.CREATE_LOCATION]: {
    titleKey: 'onboarding.step.createLocation.title',
    descriptionKey: 'onboarding.step.createLocation.description',
    href: '/admin/locations-management?action=create',
    icon: 'building-2',
  },
  [OnboardingStep.ADD_RESOURCES]: {
    titleKey: 'onboarding.step.addResources.title',
    descriptionKey: 'onboarding.step.addResources.description',
    href: '/admin/resources?action=add',
    icon: 'layout-grid',
  },
  [OnboardingStep.UPLOAD_FLOOR_PLAN]: {
    titleKey: 'onboarding.step.uploadFloorPlan.title',
    descriptionKey: 'onboarding.step.uploadFloorPlan.description',
    href: '/admin/locations/:locationId/map-editor',
    icon: 'map',
  },
  [OnboardingStep.INVITE_TEAM]: {
    titleKey: 'onboarding.step.inviteTeam.title',
    descriptionKey: 'onboarding.step.inviteTeam.description',
    href: '/admin/users',
    icon: 'users',
  },
  [OnboardingStep.MAKE_BOOKING]: {
    titleKey: 'onboarding.step.makeBooking.title',
    descriptionKey: 'onboarding.step.makeBooking.description',
    href: '/user/reservations',
    icon: 'calendar-check',
  },
  [OnboardingStep.INSTALL_CALENDAR_APP]: {
    titleKey: 'onboarding.step.installCalendarApp.title',
    descriptionKey: 'onboarding.step.installCalendarApp.description',
    href: '/admin/apps/all?category=Calendar',
    icon: 'calendar-plus',
  },
  [OnboardingStep.LINK_RESOURCE_CALENDAR]: {
    titleKey: 'onboarding.step.linkResourceCalendar.title',
    descriptionKey: 'onboarding.step.linkResourceCalendar.description',
    href: '/admin/locations/:locationId/resources/list',
    icon: 'calendar-clock',
  },
}

/**
 * Hook to fetch onboarding status
 */
export function useOnboardingStatus() {
  return useQuery({
    queryKey: ['onboarding', 'status'],
    queryFn: async () => {
      const res = await client<{ data: OnboardingStatus }>({
        method: 'get',
        url: '/api/v5/onboarding/status',
      })
      return res.data
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true, // Refetch when user comes back to check progress
  })
}

/**
 * Hook to dismiss onboarding checklist
 */
export function useDismissOnboarding() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const res = await client<{ data: { success: boolean } }>({
        method: 'post',
        url: '/api/v5/onboarding/dismiss',
      })
      return res.data
    },
    onSuccess: () => {
      // Invalidate and refetch onboarding status
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'status'] })
    },
  })
}

/**
 * Hook to reset onboarding (show again)
 */
export function useResetOnboarding() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const res = await client<{ data: { success: boolean } }>({
        method: 'post',
        url: '/api/v5/onboarding/reset',
      })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'status'] })
    },
  })
}

/**
 * Hook to invalidate onboarding status cache
 * Call this after completing actions that may affect onboarding status:
 * - Creating resources
 * - Inviting users
 * - Installing apps
 * - Connecting calendars
 */
export function useInvalidateOnboarding() {
  const queryClient = useQueryClient()
  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['onboarding', 'status'] })
  }, [queryClient])
}
